import React, { useRef, useEffect, useCallback, useReducer, useMemo } from 'react';
import { StyleSheet, View, ActivityIndicator, Platform } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import Text from '@/components/Typography/Text';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import { APP_NAME, API_HOST, BORDER_RADIUS } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import Turnstile from '@/components/Turnstile';
import SubmitButton from '@/components/Buttons/SubmitButton';
import AuthPage from '@/components/Pages/AuthPage';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import Link from '@/components/Typography/Link';
import { useTheme } from '@/context/ThemeContext';
import StatusContainer from '@/components/StatusContainer';

// Platform check
const isWeb = Platform.OS === 'web';

const createStyles = (colors) => StyleSheet.create({
  contentContainer: {
    width: '100%',
    gap: 12,
    alignItems: 'center',
  },
  formContainer: {
    width: '100%',
    gap: 8,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: BORDER_RADIUS.round,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  errorIconContainer: {
    backgroundColor: '#FEE2E2',
  },
  loadingIconContainer: {
    backgroundColor: colors.solid_color_secondary,
  },
  title: {
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 320,
    marginBottom: 12,
  },
  submitButton: {
    width: 240,
  },
  footer: {
    gap: 12,
    marginTop: 24,
    alignItems: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
  },
});

// Memoized footer links component
const FormLinks = ({ t, styles }) => (
  <View style={styles.footer}>
    <View style={styles.footerLinks}>
      <Text color='secondary'>{t('auth.resetPassword.rememberYourPassword')}</Text>
      <Link href="/login">
        {t('auth.resetPassword.loginLink')}
      </Link>
    </View>
  </View>
);

// Form reducer
const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_TOKEN_LOADING':
      return { ...state, isTokenLoading: action.payload };
    case 'SET_TOKEN_ERROR':
      return { ...state, tokenError: action.payload };
    case 'SET_TOKEN_VALID':
      return { ...state, isTokenValid: true, tokenError: '' };
    case 'SET_FIELD':
      return { ...state, [action.field]: action.payload, error: '' };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SUCCESS':
      return { ...state, isSuccess: true, isLoading: false };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  isTokenLoading: true,
  isTokenValid: false,
  tokenError: '',
  password: '',
  confirmPassword: '',
  error: '',
  isLoading: false,
  isSuccess: false,
};

// Status icon component
const StatusIcon = ({ status, styles }) => {
  if (status === 'loading') {
    return (
      <View style={[styles.iconContainer, styles.loadingIconContainer]}>
        <ActivityIndicator size="large" color="#6B7280" />
      </View>
    );
  }
  if (status === 'success') {
    return (
      <View style={[styles.iconContainer, styles.loadingIconContainer]}>
        <Icon name="check-circle" size={50} color="#10B981" />
      </View>
    );
  }

  return null;
};

StatusIcon.propTypes = {
  status: PropTypes.oneOf(['loading', 'error']).isRequired,
};

function ResetPasswordRoute() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const params = useLocalSearchParams();
  const token = params?.token;
  const turnstileRef = useRef(null);
  const styles = createStyles(colors);
  
  const [formState, dispatch] = useReducer(formReducer, initialState);

  // Memoize error messages
  const errorMessages = useMemo(() => ({
    noToken: t('auth.resetPassword.errors.noToken'),
    invalidToken: t('auth.resetPassword.errors.invalidToken'),
    verificationFailed: t('auth.resetPassword.errors.verificationFailed')
  }), [t]);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        dispatch({ type: 'SET_TOKEN_ERROR', payload: errorMessages.noToken });
        dispatch({ type: 'SET_TOKEN_LOADING', payload: false });
        return;
      }

      try {
        const response = await fetch(`${API_HOST}/json/auth/verify-password-reset-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!data?.valid) {
          dispatch({ type: 'SET_TOKEN_ERROR', payload: errorMessages.invalidToken });
        } else {
          dispatch({ type: 'SET_TOKEN_VALID' });
        }
      } catch (err) {
        dispatch({ 
          type: 'SET_TOKEN_ERROR', 
          payload: err.message || errorMessages.verificationFailed
        });
      } finally {
        dispatch({ type: 'SET_TOKEN_LOADING', payload: false });
      }
    };

    verifyToken();
  }, []);


  // Form handlers
  const handleFieldChange = useCallback((field) => (text) => {
    dispatch({ type: 'SET_FIELD', field, payload: text });
  }, []);

  const handleLogoPress = useCallback(() => {
    router.push('/');
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    if (!formState.password) {
      dispatch({ type: 'SET_ERROR', payload: t('auth.resetPassword.errors.passwordRequired') });
      return false;
    }
    if (formState.password.length < 8) {
      dispatch({ type: 'SET_ERROR', payload: t('auth.resetPassword.errors.passwordTooShort') });
      return false;
    }
    if (formState.password !== formState.confirmPassword) {
      dispatch({ type: 'SET_ERROR', payload: t('auth.resetPassword.errors.passwordsDoNotMatch') });
      return false;
    }
    return true;
  }, [formState.password, formState.confirmPassword, t]);

  const handleSubmit = useCallback(async () => {
    try {
      if (!validateForm()) return;

      dispatch({ type: 'SET_LOADING', payload: true });

      // Get turnstile token on submit
      let turnstileToken = null;
      if (isWeb) {
        try {
          turnstileToken = await turnstileRef.current?.execute();
        } catch (captchaError) {
          dispatch({ type: 'SET_ERROR', payload: t('auth.resetPassword.errors.captchaRequired') });
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
      }

      const response = await fetch(`${API_HOST}/json/auth/reset-password-with-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword1: formState.password,
          newPassword2: formState.confirmPassword,
          ...(isWeb && { turnstileToken })
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.message || t('auth.resetPassword.errors.resetFailed'));
      }

      dispatch({ type: 'SET_SUCCESS' });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || t('auth.resetPassword.errors.resetFailed') });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [formState.password, formState.confirmPassword, token, t, validateForm]);

  // Render loading state
  if (formState.isTokenLoading) {
    return (
      <AuthPage title={t('auth.resetPassword.title')} showHeader={false}>
        <MetaHead 
          title="Reset Password"
          description="Reset your Gab AI account password securely. Enter your new password to regain access to your account."
          noindex
        />
        <View style={styles.contentContainer}>
          <StatusIcon status="loading" styles={styles} />
          <Text style={styles.title}>{t('auth.resetPassword.verifyingToken')}</Text>
          <Text style={styles.subtitle}>{t('auth.resetPassword.pleaseWait')}</Text>
        </View>
        <FormLinks t={t} styles={styles} />
      </AuthPage>
    );
  }

  // Render error state
  if (formState.tokenError) {
    return (
      <AuthPage title={t('auth.resetPassword.title')} onLogoPress={handleLogoPress}>
        <MetaHead 
          title="Reset Password"
          description="Reset your Gab AI account password securely. Enter your new password to regain access to your account."
          noindex
        />
        <View style={styles.contentContainer}>
          <Text size='xxl' weight='bold' color='danger'>{t('auth.resetPassword.errorTitle')}</Text>
          <Text color='danger' style={styles.subtitle}>{formState.tokenError}</Text>
        </View>
        <FormLinks t={t} styles={styles} />
      </AuthPage>
    );
  }

  // Render success state
  if (formState.isSuccess) {
    return (
      <AuthPage title={t('auth.resetPassword.title')} onLogoPress={handleLogoPress}>
        <MetaHead 
          title="Reset Password"
          description="Reset your Gab AI account password securely. Enter your new password to regain access to your account."
          noindex
        />
        <View style={styles.contentContainer}>
          <StatusIcon status="success" styles={styles} />
          <Text size='xxl' weight='bold' style={styles.title}>{t('auth.resetPassword.successTitle')}</Text>
          <Text style={styles.subtitle}>{t('auth.resetPassword.successMessage')}</Text>
          <SubmitButton
            onPress={() => router.replace('/login')}
            title={t('auth.resetPassword.goToLogin')}
            style={styles.submitButton}
          />
        </View>
      </AuthPage>
    );
  }

  // Render form
  return (
    <AuthPage
      title={t('auth.resetPassword.title')}
      subtitle={t('auth.resetPassword.subtitle')}
      onLogoPress={handleLogoPress}
    >
      <MetaHead 
        title="Reset Password"
        description="Reset your Gab AI account password securely. Enter your new password to regain access to your account."
        noindex
      />
      <View style={styles.formContainer}>
        <FloatingLabelInput
          label={t('auth.resetPassword.passwordLabel')}
          value={formState.password}
          onChangeText={handleFieldChange('password')}
          secureTextEntry
          autoCapitalize="none"
          autoComplete="new-password"
          textContentType="newPassword"
        />

        <FloatingLabelInput
          label={t('auth.resetPassword.confirmPasswordLabel')}
          value={formState.confirmPassword}
          onChangeText={handleFieldChange('confirmPassword')}
          secureTextEntry
          autoCapitalize="none"
          autoComplete="new-password"
          textContentType="newPassword"
          onSubmitEditing={handleSubmit}
        />

        {formState.error ? (
          <StatusContainer type="error" message={formState.error} />
        ) : null}

        {isWeb && <Turnstile ref={turnstileRef} />}

        <SubmitButton
          onPress={handleSubmit}
          isLoading={formState.isLoading}
          isDisabled={!formState.password || !formState.confirmPassword}
          title={t('auth.resetPassword.submitButton')}
        />
      </View>

      <FormLinks t={t} styles={styles} />
    </AuthPage>
  );
}

export default React.memo(ResetPasswordRoute);
